import { Activity, Layout, Hash, CalendarDays, CalendarClock, AlertTriangle } from 'lucide-react';
import type { FilterDef } from './types';
import { FacetedStatusEditor, STATUS_LABELS } from './editors/FacetedStatusEditor';
import { TemplateEditor } from './editors/TemplateEditor';
import { ExternalUserIdEditor } from './editors/ExternalUserIdEditor';
import { DateRangeEditor } from './editors/DateRangeEditor';
import { CompletedNoResultsEditor } from './editors/CompletedNoResultsEditor';
import type { InterviewTypeListItemDto } from '@sara/api-client';

function formatDateRange(after?: string, before?: string): string {
  const fmt = (s: string) =>
    new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  if (after && before) return `${fmt(after)} – ${fmt(before)}`;
  if (after) return `after ${fmt(after)}`;
  if (before) return `before ${fmt(before)}`;
  return '';
}

export const FILTER_REGISTRY: FilterDef[] = [
  {
    field: 'status',
    label: 'Status',
    Icon: Activity,
    isActive: (s) => (s.status?.length ?? 0) > 0,
    clearParams: () => ({ status: undefined }),
    chipValue: (s) => (s.status ?? []).map((st) => STATUS_LABELS[st] ?? st).join(', '),
    EditorComponent: FacetedStatusEditor,
  },
  {
    field: 'template',
    label: 'Template',
    Icon: Layout,
    isActive: (s) => s.interviewTypeVersionId !== undefined,
    clearParams: () => ({ interviewTypeVersionId: undefined }),
    chipValue: (s, types: InterviewTypeListItemDto[]) => {
      const match = types.find((t) => t.latestVersionId === s.interviewTypeVersionId);
      return match?.name ?? s.interviewTypeVersionId ?? '';
    },
    EditorComponent: TemplateEditor,
  },
  {
    field: 'externalUserId',
    label: 'External user ID',
    Icon: Hash,
    isActive: (s) => s.externalUserIdSearch !== undefined,
    clearParams: () => ({ externalUserIdSearch: undefined }),
    chipValue: (s) => s.externalUserIdSearch ?? '',
    EditorComponent: ExternalUserIdEditor,
  },
  {
    field: 'created',
    label: 'Created',
    Icon: CalendarDays,
    isActive: (s) => s.createdAfter !== undefined || s.createdBefore !== undefined,
    clearParams: () => ({ createdAfter: undefined, createdBefore: undefined }),
    chipValue: (s) => formatDateRange(s.createdAfter, s.createdBefore),
    EditorComponent: (props) => (
      <DateRangeEditor afterParam="createdAfter" beforeParam="createdBefore" {...props} />
    ),
  },
  {
    field: 'expires',
    label: 'Expires',
    Icon: CalendarClock,
    isActive: (s) => s.expiresAfter !== undefined || s.expiresBefore !== undefined,
    clearParams: () => ({ expiresAfter: undefined, expiresBefore: undefined }),
    chipValue: (s) => formatDateRange(s.expiresAfter, s.expiresBefore),
    EditorComponent: (props) => (
      <DateRangeEditor afterParam="expiresAfter" beforeParam="expiresBefore" {...props} />
    ),
  },
  {
    field: 'completedNoResults',
    label: 'Completed · no results',
    Icon: AlertTriangle,
    isActive: (s) => s.completedNoResults === true,
    clearParams: () => ({ completedNoResults: undefined }),
    chipValue: () => 'No results',
    EditorComponent: CompletedNoResultsEditor,
    // Choosing this filter turns it on straight away (it's a single toggle).
    defaultParams: () => ({ completedNoResults: true }),
  },
];
